import React, {useState} from 'react';
import {useForm} from 'react-hook-form';
import {Button, message} from 'antd';
import {HttpStatusCode} from 'axios';
import CustomModal from '@component/CustomModal';
import CustomValidFormInput from '@component/form/CustomValidFormInput';
import {callChangePassword2, callFindUserPassword} from '@api/UserManagementApi';
import {ChangePassword, UserFind} from '@interface/master/UserManagementIF';
import {useMessage} from '@hook/useMessage';

interface FindPasswordPopupProps {
    onClose?: () => void;
    onOk?: () => void;
}

/**
 * 비밀번호 찾기 — 아이디 + 이름 + 휴대폰번호가 일치하면 새 비밀번호 설정 모달을 띄운다.
 * 로그인 화면의 "비밀번호찾기" 모달 내용. (운영 jar 번들에서 복원)
 */
const FindPasswordPopup = (props: FindPasswordPopupProps) => {
    const {control: findControl, register: findRegister, handleSubmit: findHandleSubmit, reset: findReset}
        = useForm<UserFind>({mode: 'onSubmit'});
    const {control: pwControl, register: pwRegister, handleSubmit: pwHandleSubmit, setError: pwSetError}
        = useForm<ChangePassword>({mode: 'onSubmit'});

    const [findInfo, setFindInfo] = useState<UserFind>({userId: '', userName: '', hpNo: ''});
    const [pwInfo, setPwInfo] = useState<ChangePassword>({userId: '', userSeq: 0, passwd: '', password_retry: ''});
    const [pwOpen, setPwOpen] = useState<boolean>(false);
    const {alert} = useMessage();

    const handleReset = () => {
        setFindInfo({userId: '', userName: '', hpNo: ''});
        findReset();
    };

    const handleFind = async (value: UserFind) => {
        try {
            const result = await callFindUserPassword(value);
            if (result.code === HttpStatusCode.Ok && result.item) {
                setPwInfo({userId: result.item.userId ?? '', userSeq: Number(result.item.userSeq), passwd: '', password_retry: ''});
                setPwOpen(true);
            } else {
                message.warning('해당 정보로 회원가입된 아이디가 없습니다.');
            }
        } catch (e) {
            message.error('관리자에게 문의바랍니다.');
        }
    };

    const handleChangePassword = async (value: ChangePassword) => {
        if (value.passwd != value.password_retry) {
            pwSetError('password_retry', {message: '패스워드가 일치하지 않습니다.'});
            return;
        }
        const result = await callChangePassword2(pwInfo);
        if (result.code === HttpStatusCode.Ok) {
            alert('비밀번호가 변경되었습니다.');
            setTimeout(() => {
                handleReset();
                setPwOpen(false);
                props.onOk && props.onOk();
                props.onClose && props.onClose();
            });
        } else {
            message.error(result.message);
        }
    };

    return (
        <div>
            <form onSubmit={findHandleSubmit(handleFind)}>
                <div>
                    <span className="tit">사용자ID</span>
                    <div className="box-inp">
                        <CustomValidFormInput
                            title={'사용자Id'}
                            control={findControl}
                            required={true}
                            onChangeValue={(v) => setFindInfo({...findInfo, userId: v})}
                            {...findRegister('userId', {required: '아이디를 입력하세요.'})}
                        />
                    </div>
                    <span className="tit">이름</span>
                    <div className="box-inp">
                        <CustomValidFormInput
                            title={'사용자이름'}
                            control={findControl}
                            onChangeValue={(v) => setFindInfo({...findInfo, userName: v})}
                            {...findRegister('userName', {required: '이름을 입력하세요.'})}
                        />
                    </div>
                    <span className="tit">휴대폰번호</span>
                    <div className="box-inp">
                        <CustomValidFormInput
                            title={'휴대폰번호'}
                            control={findControl}
                            onChangeValue={(v) => setFindInfo({...findInfo, hpNo: v})}
                            {...findRegister('hpNo', {required: '휴대폰번호를 입력하세요.'})}
                        />
                    </div>
                </div>
                <Button type="primary" style={{marginTop: 10}} onClick={findHandleSubmit(handleFind)}>비밀번호 찾기</Button>
                <Button type="primary" style={{marginTop: 10}} onClick={props.onClose}>닫기</Button>
            </form>

            <CustomModal open={pwOpen}
                         footer={null}
                         onCancel={() => {
                             handleReset();
                             setPwOpen(false);
                             props.onClose && props.onClose();
                         }}>
                <form onSubmit={pwHandleSubmit(handleChangePassword)}>
                    <div>
                        <span className="tit">변경할 비밀번호</span>
                        <div className="box-inp">
                            <CustomValidFormInput
                                title={'변경할 비밀번호'}
                                type={'password'}
                                control={pwControl}
                                required={true}
                                onChangeValue={(v) => setPwInfo({...pwInfo, passwd: v})}
                                {...pwRegister('passwd', {required: '비밀번호를 입력하세요.'})}
                            />
                        </div>
                        <span className="tit">비밀번호 확인</span>
                        <div className="box-inp">
                            <CustomValidFormInput
                                title={'비밀번호 확인'}
                                type={'password'}
                                control={pwControl}
                                required={true}
                                {...pwRegister('password_retry', {required: '비밀번호를 입력하세요.'})}
                            />
                        </div>
                    </div>
                    <Button type="primary" style={{marginTop: 10}} onClick={pwHandleSubmit(handleChangePassword)}>비밀번호 변경</Button>
                    <Button type="primary" style={{marginTop: 10}} onClick={props.onClose}>닫기</Button>
                </form>
            </CustomModal>
        </div>
    );
};

export default FindPasswordPopup;
